import React, { useState, useEffect } from 'react';
import Icon from './Icon';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const NotificationContainer = styled(motion.div)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
  max-width: 400px;
  min-width: 300px;
`;

const NotificationItem = styled(motion.div)`
  background: ${props => {
    switch (props.type) {
      case 'success':
        return 'rgba(52, 211, 153, 0.1)';
      case 'error':
        return 'rgba(248, 113, 113, 0.1)';
      case 'warning':
        return 'rgba(251, 191, 36, 0.1)';
      case 'info':
      default:
        return 'rgba(34, 211, 238, 0.1)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.type) {
      case 'success':
        return 'rgba(52, 211, 153, 0.3)';
      case 'error':
        return 'rgba(248, 113, 113, 0.3)';
      case 'warning':
        return 'rgba(251, 191, 36, 0.3)';
      case 'info':
      default:
        return 'rgba(34, 211, 238, 0.3)';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success':
        return '#34D399';
      case 'error':
        return '#F87171';
      case 'warning':
        return '#fbbf24';
      case 'info':
      default:
        return 'var(--accent-cyan)';
    }
  }};
  padding: 1rem 1.5rem;
  border-radius: var(--radius-lg);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-lg);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => {
      switch (props.type) {
        case 'success':
          return '#34D399';
        case 'error':
          return '#F87171';
        case 'warning':
          return '#fbbf24';
        case 'info':
        default:
          return 'var(--accent-cyan)';
      }
    }};
  }
`;

const NotificationIcon = styled.div`
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
  
  .title {
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }
  
  .message {
    font-size: 0.9rem;
    opacity: 0.9;
    line-height: 1.4;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: currentColor;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0.25rem;
  border-radius: var(--radius-sm);
  transition: all 0.2s ease;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ProgressBar = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: currentColor;
  opacity: 0.3;
`;

const getIcon = (type) => {
  switch (type) {
    case 'success':
      return <Icon name="check" size={16} />;
    case 'error':
      return <Icon name="close" size={16} />;
    case 'warning':
      return <Icon name="alert" size={16} />;
    case 'info':
    default:
      return <Icon name="info" size={16} />;
  }
};

const Notification = ({ notifications, removeNotification }) => {
  return (
    <NotificationContainer>
      <AnimatePresence>
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            type={notification.type}
            initial={{ opacity: 0, x: 300, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 300, scale: 0.8 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <NotificationIcon>
              {getIcon(notification.type)}
            </NotificationIcon>
            
            <NotificationContent>
              {notification.title && (
                <div className="title">{notification.title}</div>
              )}
              <div className="message">{notification.message}</div>
            </NotificationContent>
            
            <CloseButton
              onClick={() => removeNotification(notification.id)}
              aria-label="Close notification"
            >
              ×
            </CloseButton>
            
            {notification.autoClose && (
              <ProgressBar
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: notification.duration / 1000, ease: "linear" }}
              />
            )}
          </NotificationItem>
        ))}
      </AnimatePresence>
    </NotificationContainer>
  );
};

export default Notification;
